/**
 * Checklist / to-do nodes for the editor — an interactive 3-column list
 * (number | content | checkbox) inserted via Insert > Checklist. Follows the
 * same custom-node + vanilla-NodeView conventions as imageNode.ts /
 * citationNode.ts.
 *
 *  - `checklist`  — a block container holding `checklistItem+`.
 *  - `checklistItem` — one row (`content: "inline*"`, so only the middle
 *    "content" cell is editable) carrying a single `checked` boolean. Its
 *    NodeView paints the auto-number (a CSS counter — nothing stored, so it
 *    recomputes for free on add/remove), a clickable checkbox that toggles
 *    `checked`, and a per-row remove button.
 *
 * The checklist behaves like a real list for structural editing:
 *  - Enter on a non-empty item starts the next item (the default split);
 *  - Enter on an empty item, or Backspace at the start of any item, BREAKS the
 *    list — that item becomes a paragraph, splitting the checklist into the
 *    items before it and the items after it (breakChecklistAt);
 *  - ArrowUp/ArrowLeft at the very start of the first item drops a gap cursor
 *    before the checklist so you can type in front of one that sits at the top
 *    of the document (gapBeforeChecklist);
 *  - two checklists that end up directly adjacent (e.g. the separating
 *    paragraph is deleted) are auto-merged by an appendTransaction.
 *
 * Like tables/lists, neither node carries a `paraId`: a top-level checklist
 * therefore collapses to exactly one plain-text block in the flat projection
 * (pmDocToParagraphs), positionMap refuses positions inside it (no highlights
 * in a checklist), and ParaIdGuard leaves it alone — all automatic, see
 * schema.ts's header. Structure + `checked` round-trip losslessly through the
 * PM-JSON `content` sidecar (persist.ts). DOCX export is a separate concern in
 * docxExporter.ts (a static table with ☑ / ☐ text).
 *
 * The checkbox toggle dispatches setNodeMarkup the same way imageNode.ts's
 * commitWidth does, so it composes with Yjs collaboration + undo for free.
 */

import { Node, mergeAttributes, type NodeViewRenderer } from "@tiptap/core";
import { TextSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { canJoin } from "@tiptap/pm/transform";
import { splitBlock } from "@tiptap/pm/commands";
import { GapCursor } from "@tiptap/pm/gapcursor";
import type { Node as PMNode } from "@tiptap/pm/model";
import { ySyncPluginKey } from "y-prosemirror";
import type { Editor } from "@tiptap/core";

/**
 * Chrome Android is the one platform where ProseMirror deliberately drops the
 * Enter keydown (prosemirror-view input.ts: `if (android && chrome && keyCode ==
 * 13) return`), because the soft keyboard folds it into a confused sequence of
 * composition events. The keystroke instead surfaces only as a `beforeinput`
 * with inputType "insertParagraph", so our addKeyboardShortcuts Enter binding
 * never runs and a new checklist item is never started. We fill that gap with a
 * beforeinput fallback (checklistEnterFallbackPlugin), but only on Android:
 * iOS already re-dispatches a synthetic Enter keydown of its own, so handling
 * beforeinput there too would split the item twice, and desktop cancels the
 * beforeinput by preventDefault-ing the keydown the keymap handles.
 */
const isAndroid = () => typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);

export const CHECKED_GLYPH = "☑";
export const UNCHECKED_GLYPH = "☐";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    checklist: {
      /** Insert a new checklist (one empty item) at the cursor. */
      insertChecklist: () => ReturnType;
    };
  }
}

const checklistItemAttributes = {
  checked: {
    default: false,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-checked") === "true",
    renderHTML: (attrs: Record<string, any>) => ({ "data-checked": attrs.checked ? "true" : "false" }),
  },
};

/** Delete the checklistItem whose position (before the node) is `pos`. If it's
 *  the item's checklist's last one, the whole checklist goes with it. */
function removeChecklistItemAt(editor: Editor, pos: number): void {
  const view = editor.view;
  const { state } = view;
  const item = state.doc.nodeAt(pos);
  if (!item || item.type.name !== "checklistItem") return;
  const $pos = state.doc.resolve(pos); // parent = the checklist, nodeAfter = item
  const list = $pos.parent;
  let tr;
  if (list.type.name === "checklist" && list.childCount <= 1) {
    tr = state.tr.delete($pos.before($pos.depth), $pos.after($pos.depth));
  } else {
    tr = state.tr.delete(pos, pos + item.nodeSize);
  }
  view.dispatch(tr);
  view.focus();
}

/**
 * Break the checklist containing the item at `itemPos` (a position directly
 * before a checklistItem): the item becomes a paragraph in place, and the
 * checklist splits into the items before it and the items after it (either side
 * omitted when empty). The item's inline content moves into the paragraph, and
 * the cursor lands there. Shared engine for Backspace-at-start and
 * Enter-on-empty; it always leaves a paragraph between the two halves so the
 * merge plugin below doesn't immediately rejoin them.
 */
function breakChecklistAt(editor: Editor, itemPos: number): boolean {
  const { state } = editor;
  const $item = state.doc.resolve(itemPos); // parent = the checklist
  const list = $item.parent;
  if (list.type.name !== "checklist") return false;
  const listDepth = $item.depth;
  const itemIndex = $item.index();
  const listStart = $item.before(listDepth);
  const listEnd = $item.after(listDepth);
  const item = list.child(itemIndex);

  const before: PMNode[] = [];
  const after: PMNode[] = [];
  list.forEach((child, _off, index) => {
    if (index < itemIndex) before.push(child);
    else if (index > itemIndex) after.push(child);
  });

  const paragraph = state.schema.nodes.paragraph.create(null, item.content);
  const replacement: PMNode[] = [];
  if (before.length) replacement.push(list.type.create(list.attrs, before));
  replacement.push(paragraph);
  if (after.length) replacement.push(list.type.create(list.attrs, after));

  const leadingSize = before.length ? list.type.create(list.attrs, before).nodeSize : 0;
  const tr = state.tr.replaceWith(listStart, listEnd, replacement);
  tr.setSelection(TextSelection.near(tr.doc.resolve(listStart + leadingSize + 1)));
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
  return true;
}

/** Enter on an empty item breaks the list (see breakChecklistAt); a non-empty
 *  item returns false so the default split starts the next item. */
export function breakChecklistOnEnter(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || $from.parent.type.name !== "checklistItem" || $from.parent.content.size !== 0) return false;
  return breakChecklistAt(editor, $from.before($from.depth));
}

/** Backspace at the very start of any item breaks the list (see
 *  breakChecklistAt) instead of the default join, so the first item can lift
 *  out and you can put content in front of the checklist. */
export function breakChecklistOnBackspace(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || $from.parent.type.name !== "checklistItem" || $from.parentOffset !== 0) return false;
  return breakChecklistAt(editor, $from.before($from.depth));
}

/** ArrowUp/ArrowLeft at the very start of the first item drops a gap cursor
 *  before the checklist (when valid), so you can insert a paragraph/heading in
 *  front of one that sits at the top of the document. */
export function gapBeforeChecklist(editor: Editor): boolean {
  const { state } = editor;
  const { $from, empty } = state.selection;
  if (!empty || $from.parent.type.name !== "checklistItem" || $from.parentOffset !== 0) return false;
  const listDepth = $from.depth - 1;
  if ($from.node(listDepth)?.type.name !== "checklist") return false;
  // Only at the first item of a checklist that is itself the first child of its
  // parent — i.e. there's no preceding node to take a normal cursor, so a gap
  // cursor before the checklist is the only way to type in front of it.
  if ($from.index(listDepth) !== 0 || $from.index(listDepth - 1) !== 0) return false;
  const $before = state.doc.resolve($from.before(listDepth));
  editor.view.dispatch(state.tr.setSelection(new GapCursor($before)).scrollIntoView());
  return true;
}

/**
 * Android soft-keyboard Enter fallback (see IS_ANDROID above). Chrome Android
 * never delivers the Enter keydown to the keymap, so we replay the desktop Enter
 * path off the `beforeinput`/insertParagraph the keyboard fires instead: an
 * empty item breaks the list (breakChecklistOnEnter), a non-empty one splits
 * into the next item (the default splitBlock the keymap would have run). We
 * preventDefault so the browser doesn't also insert its own paragraph on top.
 */
function checklistEnterFallbackPlugin(editor: Editor): Plugin {
  return new Plugin({
    key: new PluginKey("checklistEnterFallback"),
    props: {
      handleDOMEvents: {
        beforeinput: (view, event) => {
          // Check inputType (fires once per Enter) before the UA test, so we
          // don't sniff on every keystroke's insertText beforeinput.
          if ((event as InputEvent).inputType !== "insertParagraph" || !isAndroid()) return false;
          const { $from, empty } = view.state.selection;
          if (!empty || $from.parent.type.name !== "checklistItem") return false;
          event.preventDefault();
          // breakChecklistOnEnter handles the empty-item case and returns false
          // for a non-empty one, falling through to the default split.
          return breakChecklistOnEnter(editor) || splitBlock(view.state, view.dispatch);
        },
      },
    },
  });
}

/** Row NodeView: [number] [checkbox] [editable content] [remove]. */
const checklistItemNodeView: NodeViewRenderer = ({ editor, node, getPos }) => {
  const row = document.createElement("div");
  row.className = "qda-checklist-row";

  const num = document.createElement("span");
  num.className = "qda-checklist-num";
  num.contentEditable = "false";
  row.appendChild(num);

  const check = document.createElement("span");
  check.className = "qda-checklist-check";
  check.contentEditable = "false";
  check.setAttribute("role", "checkbox");
  row.appendChild(check);

  const content = document.createElement("div");
  content.className = "qda-checklist-content";
  row.appendChild(content);

  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "qda-checklist-remove";
  remove.contentEditable = "false";
  remove.title = "Remove item";
  remove.setAttribute("aria-label", "Remove item");
  remove.textContent = "✕";
  row.appendChild(remove);

  const paint = (checked: boolean) => {
    row.classList.toggle("qda-checklist-checked", checked);
    check.textContent = checked ? CHECKED_GLYPH : UNCHECKED_GLYPH;
    check.setAttribute("aria-checked", checked ? "true" : "false");
  };
  paint(!!node.attrs.checked);

  const toggle = () => {
    if (!editor.isEditable || typeof getPos !== "function") return;
    const pos = getPos();
    if (pos == null) return;
    const view = editor.view;
    const target = view.state.doc.nodeAt(pos);
    if (!target) return;
    view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...target.attrs, checked: !target.attrs.checked }));
  };

  // preventDefault on mousedown keeps the click from moving the selection.
  check.addEventListener("mousedown", (e) => e.preventDefault());
  check.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  });
  remove.addEventListener("mousedown", (e) => e.preventDefault());
  remove.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!editor.isEditable || typeof getPos !== "function") return;
    const pos = getPos();
    if (pos != null) removeChecklistItemAt(editor, pos);
  });

  return {
    dom: row,
    contentDOM: content,
    update: (updated) => {
      if (updated.type.name !== node.type.name) return false;
      paint(!!updated.attrs.checked);
      return true;
    },
    // Only the editable content cell feeds ProseMirror; ignore mutations in the
    // number/checkbox/remove furniture we manage ourselves.
    ignoreMutation: (mutation) =>
      mutation.type !== "selection" && !content.contains(mutation.target as globalThis.Node),
    // Don't let clicks on the checkbox / remove button reach ProseMirror.
    stopEvent: (e) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.classList.contains("qda-checklist-check") || t.classList.contains("qda-checklist-remove"));
    },
  };
};

export const ChecklistItem = Node.create({
  name: "checklistItem",
  content: "inline*",
  defining: true,
  addAttributes: () => checklistItemAttributes,
  // A div (not li) so paste/parse never collides with StarterKit's listItem.
  parseHTML: () => [{ tag: "div[data-checklist-item]" }],
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { "data-checklist-item": "", class: "qda-checklist-row" }),
    0,
  ],
  addNodeView: () => checklistItemNodeView,

  // Android never fires the Enter keymap below (see checklistEnterFallbackPlugin);
  // this replays the same split/break off the soft keyboard's beforeinput.
  addProseMirrorPlugins() {
    return [checklistEnterFallbackPlugin(this.editor)];
  },

  addKeyboardShortcuts() {
    return {
      // Enter on a non-empty item falls through to the default split (starts the
      // next item); on an empty item it breaks the list, so a second Enter
      // splits the checklist in two (with a paragraph between).
      Enter: () => breakChecklistOnEnter(this.editor),
      // Backspace at the very start of any item breaks the list instead of the
      // default join — this is how you lift the first item out and get content
      // in front of the checklist.
      Backspace: () => breakChecklistOnBackspace(this.editor),
      // Escape upward/leftward past the top of the checklist into a gap cursor.
      ArrowUp: () => gapBeforeChecklist(this.editor),
      ArrowLeft: () => gapBeforeChecklist(this.editor),
    };
  },
});

/** Join any two directly-adjacent checklists into one — so deleting whatever
 *  separated them merges them (the inverse of breakChecklistAt's split). Skips
 *  Yjs remote-origin changes (like ParaIdGuard) so both peers don't both apply
 *  the structural join and diverge. */
const checklistMergePlugin = new Plugin({
  key: new PluginKey("checklistMerge"),
  appendTransaction: (transactions, _oldState, newState) => {
    if (!transactions.some((tr) => tr.docChanged)) return null;
    if (
      transactions.some(
        (tr) => (tr.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined)?.isChangeOrigin
      )
    ) {
      return null;
    }
    const boundaries: number[] = [];
    newState.doc.descendants((node, pos, parent, index) => {
      if (
        node.type.name === "checklist" &&
        parent &&
        index + 1 < parent.childCount &&
        parent.child(index + 1).type.name === "checklist"
      ) {
        boundaries.push(pos + node.nodeSize); // the point between the two checklists
      }
    });
    if (!boundaries.length) return null;
    const tr = newState.tr;
    // Right-to-left so each join leaves the earlier boundary positions valid.
    boundaries
      .sort((a, b) => b - a)
      .forEach((pos) => {
        if (canJoin(tr.doc, pos)) tr.join(pos);
      });
    return tr.docChanged ? tr : null;
  },
});

export const Checklist = Node.create({
  name: "checklist",
  group: "block",
  content: "checklistItem+",
  // A div (not ul) so paste/parse never collides with StarterKit's bulletList.
  parseHTML: () => [{ tag: "div[data-checklist]" }],
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { "data-checklist": "", class: "qda-checklist" }),
    0,
  ],

  addProseMirrorPlugins() {
    return [checklistMergePlugin];
  },

  addCommands() {
    return {
      insertChecklist:
        () =>
        ({ chain }) =>
          chain()
            .insertContent({ type: this.name, content: [{ type: "checklistItem" }] })
            .run(),
    };
  },
});
